/* eslint-disable react/require-default-props, no-console */

import React, {useEffect, useRef, useState} from 'react';
import {
  AccessibilityInfo,
  FlatList,
  Platform,
  ScrollView,
  Share,
  View,
  ViewStyle,
} from 'react-native';
import Config from 'react-native-config';
import {useSelector} from 'react-redux';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import ModalHeader from '/component/layout/ModalHeader';
import {ShareOptaviaBundleTestIds as tids} from '/constant/TestIDs';
import {EVENT_DATA, EVENT_NAMES, SCREEN_NAMES} from '/enum/tealium.enum';
import {ts} from '/provider/TranslationProvider/TranslationProvider.util';
import {variablesMealPlan} from '/redux/selector/remoteConfig.selector';
import {onyxLight} from '/theme/color/on.color';
import {openURL} from '/util/linking.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import tp from '/util/testing/testProperties';

import {styles} from './ShareOptaviaPlan.style';

interface Plan {
  description: string;
  planID: string;
  planType: string;
  showPlan: boolean;
  tagPlan: string;
  title: string;
  url: string;
}

interface ShareOptaviaPlanProps {
  onClose: () => void;
  fetchShareOptaviaPlan: () => void;
  coachId: string;
  coachAlias: string;
  hasCoachAlias: boolean;
  lastPaidRank: string;
  initialCategory?: string;
  highestRankDescription?: string;
  coachCurrentRank?: string;
}

export const ShareOptaviaPlan: React.FC<ShareOptaviaPlanProps> = ({
  onClose = () => {},
  fetchShareOptaviaPlan = () => {},
  coachId = '',
  coachAlias = '',
  hasCoachAlias = false,
  lastPaidRank = '',
  initialCategory = undefined,
  highestRankDescription = '',
  coachCurrentRank = '',
}) => {
  const textScalingProps = {allowFontScaling: true, maxFontSizeMultiplier: 2};

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [sharedPlan, setSharedPlan] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [focusedCategory, setFocusedCategory] = useState<string | null>(null);
  const [focusedPlan, setFocusedPlan] = useState<string | null>(null);
  const [focusedButton, setFocusedButton] = useState<string | null>(null);
  const [isScreenReaderEnabled, setIsScreenReaderEnabled] = useState(false);

  // Scroll measurement state
  const [scrollViewWidth, setScrollViewWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const scrollViewRef = useRef<ScrollView>(null);
  const coachIdStr = coachId?.toString();

  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.sharePlan,
  });
  const sendEventData = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.CTA_Click,
  });

  const plansData = useSelector(variablesMealPlan) || {};

  // Get visible categories
  const getVisibleCategories = () => {
    return Object.keys(plansData).filter(cat => plansData[cat]?.showCategory);
  };

  const visibleCategories = getVisibleCategories();

  // Initialize selectedCategory
  useEffect(() => {
    if (!selectedCategory && visibleCategories.length > 0) {
      const initial =
        initialCategory && plansData[initialCategory]?.showCategory
          ? initialCategory
          : visibleCategories[0];
      setSelectedCategory(initial);
    }
  }, [visibleCategories, plansData, initialCategory, selectedCategory]);

  // Index for arrow navigation
  const activeCategoryForArrows = selectedCategory;
  const activeIndex = visibleCategories.indexOf(activeCategoryForArrows);

  const canNavigatePrevious = activeIndex > 0;
  const canNavigateNext =
    activeIndex >= 0 && activeIndex < visibleCategories.length - 1;

  const isCartOrKit = () => {
    const lowerCategory = selectedCategory?.toLowerCase();
    return lowerCategory?.includes('cart') || lowerCategory?.includes('kit');
  };

  const getTitle = () => {
    const lowerCategory = selectedCategory?.toLowerCase();
    if (lowerCategory?.includes('cart')) return 'Share Cart';
    if (lowerCategory?.includes('kit')) return 'Coach Kit';
    return 'Assign Plan';
  };

  // Measure ScrollView width
  const handleScrollViewLayout = (e: any) => {
    setScrollViewWidth(e.nativeEvent.layout.width);
  };

  // Measure content width
  const handleContentSizeChange = (w: number, _h: number) => {
    setContentWidth(w);
  };

  // Scroll to category by index
  const scrollToCategory = (category: string) => {
    if (!scrollViewRef.current || scrollViewWidth === 0 || contentWidth === 0) {
      return;
    }

    const index = visibleCategories.indexOf(category);
    if (index < 0) return;

    // Calculate approximate position per tab
    const approxTabWidth = contentWidth / visibleCategories.length;
    let targetX = index * approxTabWidth;

    // Max scrollable offset
    const maxScrollX = Math.max(0, contentWidth - scrollViewWidth);

    // For the last tab, scroll to the end
    if (index === visibleCategories.length - 1) {
      targetX = maxScrollX;
    } else {
      // Clamp to valid range
      targetX = Math.min(targetX, maxScrollX);
    }

    targetX = Math.max(0, targetX);

    scrollViewRef.current.scrollTo({x: targetX, animated: true});
  };

  const navigateToPreviousTab = () => {
    if (!canNavigatePrevious) return;
    const previousCategory = visibleCategories[activeIndex - 1];
    setSelectedCategory(previousCategory);
    setFocusedCategory(previousCategory);
    requestAnimationFrame(() => {
      AccessibilityInfo.announceForAccessibility(
        `${previousCategory}, tab ${activeIndex} of ${visibleCategories.length}, selected`,
      );
    });
  };

  const navigateToNextTab = () => {
    if (!canNavigateNext) return;
    const nextCategory = visibleCategories[activeIndex + 1];
    setSelectedCategory(nextCategory);
    setFocusedCategory(nextCategory);
    requestAnimationFrame(() => {
      AccessibilityInfo.announceForAccessibility(
        `${nextCategory}, tab ${activeIndex + 2} of ${
          visibleCategories.length
        }, selected`,
      );
    });
  };

  // Handle Create Cart button click
  const handleCreateCartPress = async (plan: Plan) => {
    setSharedPlan(plan.planID);
    setSelectedPlan(plan.planID);
    const baseUrl =
      Config.ENV === 'production'
        ? 'https://optavia.com'
        : 'https://www.ccv2medifast.com';

    if (plan.title?.trim() === 'Share Coach Kit') {
      const message = `${baseUrl}/us/en/coach/${coachAlias}/sc/coachKit-sharedCart`;
      sendEventData({
        screenName: SCREEN_NAMES.sharePlan,
        CTAtext: EVENT_DATA.CTAtextCoachKit,
        CTAtype: EVENT_DATA.CTAtype,
        targetLocation: message,
        action: 'share',
        lastPaidRank: lastPaidRank,
        coachHighestRank: highestRankDescription,
        coachCurrentRank: coachCurrentRank,
        coachID: coachIdStr,
      });
      try {
        await Share.share({message});
      } catch (error) {
        console.error('Error sharing cart:', error);
      }
    } else {
      const location = `${baseUrl}/us/en/search?q=`;
      sendEventData({
        screenName: SCREEN_NAMES.sharePlan,
        CTAtext: EVENT_DATA.CTAtextCreateCart,
        CTAtype: EVENT_DATA.CTAtype,
        targetLocation: location,
        action: 'create',
        lastPaidRank: lastPaidRank,
        coachHighestRank: highestRankDescription,
        coachCurrentRank: coachCurrentRank,
        coachID: coachIdStr,
      });
      openURL('url', location);
    }
  };

  const onPlanShare = (plan: Plan): void => {
    setSharedPlan(plan.planID);
    setSelectedPlan(plan.planID);

    shareOptaviaBundle(plan);
    sendEvent({
      screenName: SCREEN_NAMES.sharePlan,
      plan: plan.tagPlan,
      lastPaidRank: lastPaidRank,
    });
  };

  const shareOptaviaBundle = async (plan: Plan): Promise<void> => {
    const {title, url} = plan;
    try {
      const formattedUrl = url
        .replace('{coachAlias}', coachAlias)
        .replace('{coachid}', coachId);

      const message = `I would like to share the ${title}, please click the link below: \n${formattedUrl}`;

      await Share.share({message});
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Ensure selected category is valid
  useEffect(() => {
    if (!selectedCategory) return;
    const isValidCategory = plansData[selectedCategory]?.showCategory;

    if (!isValidCategory) {
      const nextValidCategory = Object.keys(plansData).find(
        category => plansData[category]?.showCategory,
      );

      if (nextValidCategory) {
        setSelectedCategory(nextValidCategory);
      }
    }
  }, [plansData, selectedCategory]);

  // Scroll when category changes
  useEffect(() => {
    if (selectedCategory && scrollViewWidth > 0 && contentWidth > 0) {
      scrollToCategory(selectedCategory);
    }
  }, [selectedCategory, scrollViewWidth, contentWidth]);

  useEffect(() => {
    if (focusedCategory && scrollViewWidth > 0 && contentWidth > 0) {
      scrollToCategory(focusedCategory);
    }
  }, [focusedCategory, scrollViewWidth, contentWidth]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchShareOptaviaPlan();
      } catch (error) {
        console.error('Error fetching ShareOptaviaPlan:', error);
      }
    };

    if (!hasCoachAlias) {
      fetchData();
    }
  }, [hasCoachAlias, fetchShareOptaviaPlan]);

  useEffect(() => {
    const checkScreenReader = async () => {
      const enabled = await AccessibilityInfo.isScreenReaderEnabled();
      setIsScreenReaderEnabled(enabled);
    };

    checkScreenReader();

    const subscription = AccessibilityInfo.addEventListener(
      'screenReaderChanged',
      setIsScreenReaderEnabled,
    );

    return () => {
      subscription.remove();
    };
  }, []);

  const filteredPlans = plansData[selectedCategory]?.showCategory
    ? plansData[selectedCategory]?.plans?.filter(
        (plan: Plan) => plan.showPlan,
      ) || []
    : [];

  const getPreviousCategoryName = () => {
    return canNavigatePrevious ? visibleCategories[activeIndex - 1] : null;
  };

  const getNextCategoryName = () => {
    return canNavigateNext ? visibleCategories[activeIndex + 1] : null;
  };

  const baseTitle = ts('AssignPlan', getTitle());

  const headerLabel =
    Platform.OS === 'ios' ? `${baseTitle} heading` : baseTitle;

  const renderItemForVoiceOver = ({item}: {item: Plan}) => {
    const accessibilityLabel = `${
      selectedCategory === 'Weight Loss'
        ? `Optimal ${selectedCategory}`
        : selectedCategory
    }${!isCartOrKit() ? `, ${item?.title}` : ''}, ${item?.description}`;

    return (
      <View
        key={item.planID}
        style={[
          styles.planCard,
          selectedPlan === item.planID && styles.selectedPlanCard,
          {position: 'relative'},
        ]}
        focusable={true}
        testID={`plan-item-${item.planID}`}>
        <TouchableOpacity
          accessible={true}
          accessibilityRole="radio"
          accessibilityState={{selected: selectedPlan === item.planID}}
          accessibilityLabel={accessibilityLabel}
          accessibilityHint="Double tap to select this plan"
          onPress={e => {
            e?.stopPropagation?.();
            setSelectedPlan(item.planID);
            setSharedPlan(null);
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            elevation: 1,
          }}
        />
        <View pointerEvents="box-none" accessible={false}>
          <Text
            {...textScalingProps}
            style={[
              styles.planTitle,
              selectedPlan === item.planID && styles.planTitleSelected,
            ]}
            isBold
            testID={tids.planCardCategory}
            accessible={false}
            importantForAccessibility="no">
            {selectedCategory === 'Weight Loss'
              ? `Optimal ${selectedCategory}`
              : selectedCategory}
          </Text>

          {!isCartOrKit() && (
            <View accessible={false}>
              <Text
                {...textScalingProps}
                style={styles.planName}
                testID={tids.planCardTitle}
                accessible={false}
                importantForAccessibility="no">
                {item?.title}
              </Text>
            </View>
          )}
        </View>

        <View pointerEvents="box-none" accessible={false}>
          <Text
            {...textScalingProps}
            style={styles.planDescription}
            testID={tids.planCardDescription}
            accessible={false}
            importantForAccessibility="no">
            {item?.description}
          </Text>
        </View>

        {/* SHARE / ADD BUTTON */}
        <View style={{zIndex: 10, elevation: 10}} pointerEvents="box-none">
          <KeyboardFocusable
            accessible={true}
            focusable={true}
            importantForAccessibility="yes"
            accessibilityRole="button"
            accessibilityLabel={
              isCartOrKit() ? `Add ${item.title}` : `Share ${item.title}`
            }
            accessibilityHint={
              isCartOrKit()
                ? 'Double tap to add this plan'
                : 'Double tap to share this plan'
            }
            focusRingStyle={styles.selectButtonFocused}
            style={[
              sharedPlan === item.planID
                ? styles.selectedButton
                : styles.selectButton,
            ]}
            onPress={e => {
              e?.stopPropagation?.();
              isCartOrKit() ? handleCreateCartPress(item) : onPlanShare(item);
            }}
            testID={`plan-item-${item.planID}-share`}
            pointerEvents="auto">
            {isCartOrKit() ? (
              <View style={styles.buttonWithIcon} accessible={false}>
                <Text
                  {...textScalingProps}
                  style={
                    sharedPlan === item.planID
                      ? styles.selectedButtonText
                      : styles.selectButtonText
                  }
                  isBold
                  accessible={false}
                  importantForAccessibility="no">
                  {item.title}
                </Text>
                <Icon
                  name="logout"
                  style={styles.buttonIcon}
                  accessible={false}
                />
              </View>
            ) : (
              <Text
                {...textScalingProps}
                style={
                  sharedPlan === item.planID
                    ? styles.selectedButtonText
                    : styles.selectButtonText
                }
                isBold
                accessible={false}
                importantForAccessibility="no">
                {`Share ${item.title}`}
              </Text>
            )}
          </KeyboardFocusable>
        </View>
      </View>
    );
  };

  const renderItemForScreenReader = ({item}) => {
    const categoryLabel =
      selectedCategory === 'Weight Loss'
        ? `Optimal ${selectedCategory}`
        : selectedCategory;

    const accessibilityLabel = `${
      categoryLabel?.trim() === item.title?.trim()
        ? item.title?.trim()
        : `${categoryLabel?.trim()}, ${item.title?.trim()}`
    }, ${item.description}`;

    return (
      <KeyboardFocusable
        focusable={true}
        accessible={true}
        accessibilityRole="radio"
        accessibilityState={{selected: selectedPlan === item.planID}}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint="Double tap to select this plan"
        focusRingStyle={styles.planCardFocused}
        style={[
          styles.planCard,
          selectedPlan === item.planID && styles.selectedPlanCard,
        ]}
        onFocus={() => setFocusedPlan(item.planID)}
        onBlur={() => setFocusedPlan(null)}
        onPress={() => {
          setSelectedPlan(item.planID);
          setSharedPlan(null);
        }}>
        <View pointerEvents="box-none" accessible={false}>
          <Text
            {...textScalingProps}
            style={[
              styles.planTitle,
              selectedPlan === item.planID && styles.planTitleSelected,
              focusedPlan === item.planID && styles.planTitleFocused,
            ]}
            isBold
            testID={tids.planCardCategory}
            accessible={false}
            importantForAccessibility="no">
            {selectedCategory === 'Weight Loss'
              ? `Optimal ${selectedCategory}`
              : selectedCategory}
          </Text>

          {!isCartOrKit() && (
            <View accessible={false}>
              <Text
                {...textScalingProps}
                style={[
                  styles.planName,
                  focusedPlan === item.planID && styles.planNameFocused,
                ]}
                isBold={focusedPlan === item.planID}
                testID={tids.planCardTitle}
                accessible={false}
                importantForAccessibility="no">
                {item?.title}
              </Text>
            </View>
          )}
        </View>

        <View pointerEvents="box-none" accessible={false}>
          <Text
            {...textScalingProps}
            style={[
              styles.planDescription,
              focusedPlan === item.planID && styles.planDescriptionFocused,
            ]}
            isBold={focusedPlan === item.planID}
            testID={tids.planCardDescription}
            accessible={false}
            importantForAccessibility="no">
            {item?.description}
          </Text>
        </View>

        {/* SHARE / ADD BUTTON */}
        <View style={{zIndex: 10, elevation: 10}} pointerEvents="box-none">
          <KeyboardFocusable
            accessible={true}
            focusable={true}
            importantForAccessibility="yes"
            accessibilityRole="button"
            accessibilityLabel={
              isCartOrKit() ? `Add ${item.title}` : `Share ${item.title}`
            }
            accessibilityHint={
              isCartOrKit()
                ? 'Double tap to add this plan'
                : 'Double tap to share this plan'
            }
            focusRingStyle={styles.selectButtonFocused}
            style={[
              sharedPlan === item.planID
                ? styles.selectedButton
                : styles.selectButton,
            ]}
            onFocus={() => setFocusedButton(item.planID)}
            onBlur={() => setFocusedButton(null)}
            onPress={e => {
              e?.stopPropagation?.();
              isCartOrKit() ? handleCreateCartPress(item) : onPlanShare(item);
            }}
            testID={`plan-item-${item.planID}-share`}
            pointerEvents="auto">
            {isCartOrKit() ? (
              <View style={styles.buttonWithIcon} accessible={false}>
                <Text
                  {...textScalingProps}
                  style={[
                    sharedPlan === item.planID
                      ? styles.selectedButtonText
                      : styles.selectButtonText,
                    focusedButton === item.planID &&
                      styles.selectButtonTextFocused,
                  ]}
                  isBold
                  accessible={false}
                  importantForAccessibility="no">
                  {item.title}
                </Text>
                <Icon
                  name="logout"
                  style={styles.buttonIcon}
                  accessible={false}
                />
              </View>
            ) : (
              <Text
                {...textScalingProps}
                style={[
                  sharedPlan === item.planID
                    ? styles.selectedButtonText
                    : styles.selectButtonText,
                  focusedButton === item.planID &&
                    styles.selectButtonTextFocused,
                ]}
                isBold
                accessible={false}
                importantForAccessibility="no">
                {`Share ${item.title}`}
              </Text>
            )}
          </KeyboardFocusable>
        </View>
      </KeyboardFocusable>
    );
  };

  const renderItem = isScreenReaderEnabled
    ? renderItemForVoiceOver
    : renderItemForScreenReader;

  return (
    <ModalHeader
      onClose={onClose}
      isTopLineTitle={true}
      actionLabel={'Learn more'}
      accessibilityRole="link"
      isProfileScreen
      accessibilityLabel={headerLabel}
      importantForAccessibility="yes"
      onActionPress={() =>
        openURL(
          'url',
          'https://answers.optavia.com/help/s/article/program-guides-US?language=en_US',
        )
      }
      closeIconName={'chevron-left'}
      closeIconStyle={{color: onyxLight}}
      title={baseTitle}
      style={{base: styles.base}}>
      <View style={styles.container}>
        <View style={styles.tabNavigationContainer}>
          <KeyboardFocusable
            focusRingStyle={styles.navigationArrowFocused}
            style={[
              styles.navigationArrow,
              !canNavigatePrevious && styles.navigationArrowDisabled,
            ]}
            onPress={navigateToPreviousTab}
            disabled={!canNavigatePrevious}
            accessible={canNavigatePrevious}
            focusable={canNavigatePrevious}
            accessibilityRole="button"
            accessibilityLabel={
              canNavigatePrevious
                ? `Go to previous tab: ${getPreviousCategoryName()}`
                : `No previous categories available. Current selected tab: ${selectedCategory}`
            }
            accessibilityHint={
              canNavigatePrevious
                ? 'Double tap to go to the previous tab'
                : 'This is the first tab'
            }
            accessibilityState={{disabled: !canNavigatePrevious}}
            testID="left-arrow-button">
            <Text
              {...textScalingProps}
              accessible={false}
              style={
                Platform.OS === 'ios'
                  ? styles.navigationArrowTextIOS
                  : styles.navigationArrowText
              }>
              ‹
            </Text>
          </KeyboardFocusable>
          {/* ScrollView for tabs */}
          <ScrollView
            ref={scrollViewRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tabScrollView}
            contentContainerStyle={styles.categoryContainer}
            onLayout={handleScrollViewLayout}
            onContentSizeChange={handleContentSizeChange}>
            {visibleCategories.map((category, index) => (
              <KeyboardFocusable
                accessible={true}
                focusable={true}
                accessibilityRole="tab"
                accessibilityLabel={`Tab ${category}, ${index + 1} of ${
                  visibleCategories.length
                }`}
                accessibilityHint={
                  selectedCategory !== category
                    ? 'Double tap to select this category'
                    : ''
                }
                accessibilityState={{
                  selected: selectedCategory === category,
                }}
                key={category}
                focusRingStyle={styles.categoryButtonFocused}
                style={
                  [
                    styles.categoryButton,
                    selectedCategory === category
                      ? styles.categoryButtonSelected
                      : undefined,
                  ] as ViewStyle[]
                }
                onFocus={() => {
                  setFocusedCategory(category);
                }}
                onBlur={() => {
                  setFocusedCategory(null);
                }}
                onPress={() => {
                  setSelectedCategory(category);
                  requestAnimationFrame(() => {
                    AccessibilityInfo.announceForAccessibility(
                      `${category}, tab ${index + 1} of ${
                        visibleCategories.length
                      }, selected`,
                    );
                  });
                }}
                testID={`${tids.categoryButton}-${category}`}
                {...tp(`${tids.categoryButton}-${category}`)}>
                <View>
                  <Text
                    {...textScalingProps}
                    accessible={false}
                    importantForAccessibility="no"
                    style={[
                      styles.categoryText,
                      selectedCategory === category && styles.categoryText,
                      focusedCategory === category &&
                        styles.categoryTextFocused,
                    ]}
                    isBold
                    testID={category}>
                    {category}
                  </Text>
                </View>
              </KeyboardFocusable>
            ))}
          </ScrollView>
          <KeyboardFocusable
            focusRingStyle={styles.navigationArrowFocused}
            style={[
              styles.navigationArrow,
              !canNavigateNext && styles.navigationArrowDisabled,
            ]}
            onPress={navigateToNextTab}
            disabled={!canNavigateNext}
            accessible={canNavigateNext}
            focusable={canNavigateNext}
            accessibilityRole="button"
            accessibilityLabel={
              canNavigateNext
                ? `Go to next tab: ${getNextCategoryName()}`
                : `No more categories available. Current selected tab: ${selectedCategory}`
            }
            accessibilityHint={
              canNavigateNext
                ? 'Double tap to go to the next tab'
                : 'This is the last tab'
            }
            accessibilityState={{disabled: !canNavigateNext}}
            testID="right-arrow-button">
            <Text
              {...textScalingProps}
              accessible={false}
              style={
                Platform.OS === 'ios'
                  ? styles.navigationArrowTextIOS
                  : styles.navigationArrowText
              }>
              ›
            </Text>
          </KeyboardFocusable>
        </View>

        <View style={styles.separator} />

        {!isCartOrKit() &&
        (plansData[selectedCategory]?.showCategory ||
          filteredPlans.length > 0) ? (
          <KeyboardFocusable
            focusable={true}
            accessible={true}
            accessibilityRole="header"
            accessibilityLabel={`${selectedCategory} selected category`}
            focusRingStyle={styles.sectionTitleFocused}>
            <View
              accessible={true}
              importantForAccessibility="yes"
              accessibilityRole="header"
              accessibilityLabel={`${selectedCategory} selected category`}>
              <Text
                {...textScalingProps}
                accessible={false}
                importantForAccessibility="no"
                style={styles.sectionTitle}
                isBold
                testID={tids.selectedCategoryTitle}>
                {selectedCategory}
              </Text>
            </View>
          </KeyboardFocusable>
        ) : null}

        <FlatList
          data={filteredPlans}
          keyExtractor={item => item.planID}
          contentContainerStyle={styles.flatList}
          testID="category-plans-list"
          renderItem={renderItem}
        />
      </View>
    </ModalHeader>
  );
};

export default ShareOptaviaPlan;
